import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import LoginDebug from "./pages/LoginDebug";
import LoginSip from "./pages/LoginSip";
import RegisterDebug from "./pages/RegisterDebug";
import RegisterSip from "./pages/RegisterSip";
import Dashboard from "./pages/Dashboard";
import Trends from "./pages/Trends";
import Tools from "./pages/Tools";
import Activity from "./pages/Activity";
import Wallet from "./pages/Wallet";
import Scanner from "./pages/Scanner";
import OsintScanner from "./pages/OsintScanner";
import Terms from "./pages/legal/Terms";
import Privacy from "./pages/legal/Privacy";
import AUP from "./pages/legal/AUP";
import Refund from "./pages/legal/Refund";
import KYC from "./pages/KYC";
import BugTester from "./pages/BugTester";
import SipTester from "./pages/SipTester";
import BugScoutLanding from "./pages/BugScoutLanding";
import SipLanding from "./pages/SipLanding";
import KavaLanding from "./pages/KavaLanding";
import About from "./pages/company/About";
import Blog from "./pages/company/Blog";
import BlogPost from "./pages/company/BlogPost";
import APIDocs from "./pages/company/APIDocs";
import Contact from "./pages/company/Contact";
import Security from "./pages/legal/Security";
import MasterLogin from "./pages/master/MasterLogin";
import MasterDashboard from "./pages/master/MasterDashboard";
import UserDetail from "./pages/master/UserDetail";
import usePresenceHeartbeat from "./hooks/usePresenceHeartbeat";
import SipDialplanPage from "./pages/SipDialplanPage";
import TeamPage from "./pages/TeamPage";

import { getToken, clearAllTokens, migrateOldTokens } from "./utils/tokenStorage";
import { getBrand, getTheme } from "./utils/branding";
import { useIdleTimeout } from "./hooks/useIdleTimeout";
import IdleWarningModal from "./components/IdleWarningModal";
import OnboardingGuide from "./components/OnboardingGuide";
const PrivateRoute = ({ children }) => {
  usePresenceHeartbeat();
  const [valid, setValid] = React.useState(null);
  const token = getToken("aegis_token");

  const handleIdleLogout = React.useCallback(() => {
    clearAllTokens();
    window.location.href = "/login?reason=idle";
  }, []);

  const idleHook = useIdleTimeout({
    timeoutMinutes: 30,
    warningMinutes: 1,
    onIdle: handleIdleLogout,
    enabled: valid === true
  });

  React.useEffect(() => {
    if (!token) { setValid(false); return; }
    fetch("/api/auth/me", {
      headers: { "Authorization": `Bearer ${token}` },
      cache: "no-store"
    })
    .then(r => {
      if (r.ok) setValid(true);
      else { clearAllTokens(); setValid(false); }
    })
    .catch(() => setValid(false));

    // Re-validate on window focus (browser restore, tab switch back)
    const onFocus = () => {
      const t = getToken("aegis_token");
      if (!t) return;
      fetch("/api/auth/me", { headers: { "Authorization": `Bearer ${t}` }, cache: "no-store" })
        .then(r => { if (!r.ok) { clearAllTokens(); window.location.href = "/login?expired=1"; } })
        .catch(() => {});
    };
    window.addEventListener("focus", onFocus);

    // Back button / bfcache fix - reload on history navigation restore
    const onPageShow = (event) => {
      if (event.persisted) {
        // Page restored from bfcache - force fresh session check
        const t = getToken("aegis_token");
        if (!t) {
          window.location.href = "/login";
        } else {
          window.location.reload();
        }
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [token]);

  if (valid === null) {
    const b = getBrand();
    return (
      <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:b.bg,color:b.primary,fontFamily:"'JetBrains Mono', monospace",letterSpacing:2}}>
        Verifying session...
      </div>
    );
  }
  if (!valid) return <Navigate to="/login" replace />;
  return (
    <React.Fragment>
      {children}
      <IdleWarningModal
        open={idleHook.showWarning}
        warningSeconds={60}
        onStay={idleHook.extend}
        onLogout={handleIdleLogout}
      />
    </React.Fragment>
  );
};

// Public pages (landing / login / register): if already signed in, keep the user
// inside the app. Handles the browser Back button + bfcache restore so a logged-in
// user can't land back on the pre-login pages.
const PublicOnlyRoute = ({ children }) => {
  const token = getToken("aegis_token");

  React.useEffect(() => {
    const onPageShow = (event) => {
      // bfcache restore doesn't re-run React, so a mount-time redirect isn't enough.
      if (event.persisted && getToken("aegis_token")) {
        window.location.replace("/dashboard");
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  if (token) return <Navigate to="/dashboard" replace />;
  return children;
};

export default function App() {
  // One-time migration: clear stale localStorage tokens
  React.useEffect(() => {
    const cleaned = migrateOldTokens();
    if (cleaned > 0) {
      console.log(`[Auth] Cleared ${cleaned} stale localStorage token(s)`);
      window.location.reload();
    }
  }, []);

  const brand = getBrand();
  const theme = getTheme(); // override-aware (subdomain or ?theme=)
  const isDebug = theme === 'bugscout';
  const isSip = theme === 'sip';
  const isAegis = theme === 'aegis';

  return (
    <BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: brand.cardBg,
            color: brand.textPrimary,
            border: `1px solid ${brand.cardBorder}`,
            backdropFilter: "blur(18px)",
            fontFamily: "'Urbanist', sans-serif"
          }
        }}
      />
      <Routes>
        {/* DEBUG SUBDOMAIN */}
        {isDebug && <Route path="/" element={<BugScoutLanding />} />}
        {isDebug && <Route path="/login" element={<LoginDebug />} />}
        {isDebug && <Route path="/register" element={<RegisterDebug />} />}
        {isDebug && <Route path="/dashboard" element={<PrivateRoute><BugTester /></PrivateRoute>} />}
        {isDebug && <Route path="/kyc" element={<PrivateRoute><KYC /></PrivateRoute>} />}
        {isDebug && <Route path="/wallet" element={<PrivateRoute><Wallet /></PrivateRoute>} />}
        {isDebug && <Route path="/wallet" element={<PrivateRoute><Wallet /></PrivateRoute>} />}

        {/* SIP SUBDOMAIN */}
        {isSip && <Route path="/" element={<SipLanding />} />}
        {isSip && <Route path="/login" element={<LoginSip />} />}
        {isSip && <Route path="/register" element={<RegisterSip />} />}
        {isSip && <Route path="/dashboard" element={<PrivateRoute><SipTester /></PrivateRoute>} />}
        {isSip && <Route path="/dialplan" element={<PrivateRoute><SipDialplanPage /></PrivateRoute>} />}
        {isSip && <Route path="/kyc" element={<PrivateRoute><KYC /></PrivateRoute>} />}
        {isSip && <Route path="/wallet" element={<PrivateRoute><Wallet /></PrivateRoute>} />}
        {isSip && <Route path="/wallet" element={<PrivateRoute><Wallet /></PrivateRoute>} />}

        {/* AEGIS SUBDOMAIN (default) */}
        {isAegis && <Route path="/" element={<PublicOnlyRoute><Landing /></PublicOnlyRoute>} />}
        {isAegis && <Route path="/team" element={<PrivateRoute><TeamPage /></PrivateRoute>} />}
        {isAegis && <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />}
        {isAegis && <Route path="/register" element={<PublicOnlyRoute><Register /></PublicOnlyRoute>} />}
        {isAegis && <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />}
        {isAegis && <Route path="/scan" element={<PrivateRoute><Scanner /></PrivateRoute>} />}
        {isAegis && <Route path="/trends" element={<PrivateRoute><Trends /></PrivateRoute>} />}
        {isAegis && <Route path="/activity" element={<PrivateRoute><Activity /></PrivateRoute>} />}
        {isAegis && <Route path="/osint" element={<PrivateRoute><OsintScanner /></PrivateRoute>} />}
        {isAegis && <Route path="/kyc" element={<PrivateRoute><KYC /></PrivateRoute>} />}
        {isAegis && <Route path="/wallet" element={<PrivateRoute><Wallet /></PrivateRoute>} />}

        {/* MASTER (Aegis only — accessed via direct URL) */}
        <Route path="/mascop-2k26" element={<MasterLogin />} />
        <Route path="/mascop-2k26/dashboard" element={<MasterDashboard />} />
        <Route path="/mascop-2k26/user/:userId" element={<UserDetail />} />

        {/* SHARED PAGES (work on all subdomains) */}
        {/* KAVA — next product landing. Lives on a shared route for now; promote
            to its own subdomain by adding a 'kava' theme + nginx site + DNS. */}
        <Route path="/kava" element={<KavaLanding />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/aup" element={<AUP />} />
        <Route path="/refund" element={<Refund />} />
        <Route path="/security" element={<Security />} />
        <Route path="/about" element={<About />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/api-docs" element={<APIDocs />} />
        <Route path="/docs" element={<APIDocs />} />
        <Route path="/api" element={<APIDocs />} />
        <Route path="/contact" element={<Contact />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {/* Voice onboarding guide — Aegis only. Renders inside the router so it
          can read the current route and navigate the user when it helps. */}
      {isAegis && <OnboardingGuide />}
    </BrowserRouter>
  );
}
